import { useState, useRef, useEffect, memo } from 'react';
import { trackInputKeystroke, trackCallbackInvocation, trackInputRender } from '../../utils/inputDebugger';

// Check if debugging is enabled ONCE at module level (only URL parameter, no localStorage)
const isDebugEnabled = typeof window !== 'undefined' && 
  new URLSearchParams(window.location.search).get('debugInputs') === 'true';

const OptimizedInputComponent = ({ 
  id, 
  value, 
  placeholder, 
  onChange, 
  className, 
  type = "text", 
  required = false, 
  maxLength,
  min,
  max,
  rows
}) => {
  const [localValue, setLocalValue] = useState(value);
  const prevValueRef = useRef(value);
  const prevLocalValueRef = useRef(value);
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  const renderCountRef = useRef(0);
  
  // Track renders for debugging
  useEffect(() => {
    if (isDebugEnabled) {
      renderCountRef.current += 1;
      
      // Detect what triggered the render
      let trigger = 'parent re-render or memo bypass';
      const valueChanged = value !== prevValueRef.current;
      const localValueChanged = localValue !== prevLocalValueRef.current;
      
      if (valueChanged && localValueChanged) {
        trigger = 'value prop + localValue state changed';
      } else if (valueChanged) {
        trigger = 'value prop changed';
      } else if (localValueChanged) {
        trigger = 'localValue state changed';
      }
      
      trackInputRender(id, localValue, {
        value,
        localValue,
        renderNumber: renderCountRef.current,
        isTyping: isTypingRef.current,
        trigger
      });
      
      // Update refs for next comparison
      prevValueRef.current = value;
      prevLocalValueRef.current = localValue;
    }
  }); // Run on every render when debugging is enabled
  
  // Sync value prop to local state (only when not typing). Force sync when value prop changes but localValue doesn't match (cancel/reset)
  useEffect(() => {
    // Only sync if value prop actually changed
    if (value !== prevValueRef.current) {
      // If localValue doesn't match new value, this is likely a cancel/reset - always sync even if typing
      const isCancelOrReset = localValue !== value;
      
      // Skip sync if user is actively typing (prevents lag when Redux updates catch up). Only sync if cancel/reset operation
      if (isTypingRef.current && !isCancelOrReset) {
        // User is typing - don't sync Redux updates, they'll catch up later
        prevValueRef.current = value;
        return;
      }
      
      // Clear typing state if this is a cancel/reset operation
      if (isCancelOrReset && isTypingRef.current) {
        isTypingRef.current = false;
        if (typingTimerRef.current) {
          clearTimeout(typingTimerRef.current);
          typingTimerRef.current = null;
        }
      }
      
      // Debug tracking (only call if enabled - zero overhead when disabled)
      if (isDebugEnabled) {
        trackCallbackInvocation(id, 'value prop changed', {
          from: prevValueRef.current,
          to: value,
          isTyping: isTypingRef.current,
          isCancelOrReset
        });
      }
      
      // Update local state immediately when value prop changes This ensures cancel operations sync properly
      prevValueRef.current = value;
      if (localValue !== value) {
        setLocalValue(value);
      }
    }
  }, [value, localValue, id]);

  const inputValue = localValue === null || localValue === undefined ? "" : localValue;

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    // OPTIMIZATION: Skip if value hasn't actually changed (prevents unnecessary work)
    if (localValue === newValue) {
      return;
    }
    
    // Debug tracking (only call if enabled - zero overhead when disabled)
    if (isDebugEnabled) {
      trackInputKeystroke(id, newValue, e);
    }
    
    // Update local state immediately for instant UI feedback
    setLocalValue(newValue);
    isTypingRef.current = true;
    
    // Clear existing timer
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    
    // Call onChange handler (triggers parent update logic)
    if (onChange) {
      onChange(e);
    }
    
    // Reset typing flag after 1500ms of no typing
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      typingTimerRef.current = null;
    }, 1500);
  };
  
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  const inputProps = {
    id,
    value: inputValue,
    placeholder,
    onChange: handleInputChange,
    className,
    required,
    ...(rows ? { rows } : { type, maxLength, min, max })
  };

  return (
    <div className="relative">
      {rows ? (
        <textarea {...inputProps} />
      ) : (
        <input {...inputProps} />
      )}
    </div>
  );
};

// Custom comparison function for memo - only re-render if props that affect THIS input changed OPTIMIZED: Ignore onChange function changes (they're recreated but functionally equivalent)
const areOptimizedInputPropsEqual = (prevProps, nextProps) => {
  // Always re-render if ID changes (different input)
  if (prevProps.id !== nextProps.id) return false;
  
  // Always re-render if value changes (this is the main prop we care about)
  if (prevProps.value !== nextProps.value) return false;
  
  // Re-render if other rendering props change
  if (prevProps.placeholder !== nextProps.placeholder) return false;
  if (prevProps.className !== nextProps.className) return false;
  if (prevProps.type !== nextProps.type) return false;
  if (prevProps.required !== nextProps.required) return false;
  if (prevProps.maxLength !== nextProps.maxLength) return false;
  if (prevProps.min !== nextProps.min) return false;
  if (prevProps.max !== nextProps.max) return false;
  if (prevProps.rows !== nextProps.rows) return false;
  
  // IGNORE onChange changes - functions are recreated but functionally equivalent This prevents unnecessary re-renders when parent components re-create handlers The onChange handler uses the latest closure anyway, so this is safe
  
  return true; // Props are equal, skip re-render
};

export const OptimizedInput = memo(OptimizedInputComponent, areOptimizedInputPropsEqual);
OptimizedInput.displayName = 'OptimizedInput';
